// Runs jobs for draft cards one at a time, in the order requested. The Studio keeps one queue for
// pictures and one for fun facts. A queue owns its list of waiting jobs so that a card can be cancelled
// before money is spent on it, and so that a failure that will repeat for every card stops the rest.

export interface JobQueueState {
  running: string | null;
  waiting: string[];
}

export interface JobQueueDeps {
  runJob: (draftId: string) => Promise<void>;
  onChange: (state: JobQueueState) => void;
  onError: (draftId: string, err: unknown) => void;
  // True for a failure that every remaining job would hit too (for example the engine refusing the key)
  isFatal: (err: unknown) => boolean;
  onSkipped: (draftIds: string[], err: unknown) => void;
}

export type JobState = 'idle' | 'waiting' | 'running';

export const jobStateOf = (state: JobQueueState, draftId: string): JobState => {
  if (state.running === draftId) return 'running';
  return state.waiting.indexOf(draftId) !== -1 ? 'waiting' : 'idle';
};

export const createJobQueue = (deps: JobQueueDeps) => {
  let running: string | null = null;
  let waiting: string[] = [];

  const state = (): JobQueueState => ({ running, waiting: [...waiting] });
  const emit = () => deps.onChange(state());

  const pump = async () => {
    if (running !== null) return;

    while (waiting.length > 0) {
      const draftId = waiting.shift() as string;
      running = draftId;
      emit();

      try {
        await deps.runJob(draftId);
      } catch (err) {
        deps.onError(draftId, err);
        if (deps.isFatal(err) && waiting.length > 0) {
          const skipped = waiting;
          waiting = [];
          deps.onSkipped(skipped, err);
        }
      }

      running = null;
      emit();
    }
  };

  return {
    // Returns false when that card already has a job running or waiting
    enqueue(draftId: string): boolean {
      if (running === draftId || waiting.indexOf(draftId) !== -1) return false;
      waiting.push(draftId);
      emit();
      pump();
      return true;
    },

    // Removes one card's waiting job; a job that is already running finishes
    cancel(draftId: string): void {
      if (waiting.indexOf(draftId) === -1) return;
      waiting = waiting.filter(id => id !== draftId);
      emit();
    },

    // Drops every waiting job at once; the running one finishes
    cancelWaiting(): void {
      if (waiting.length === 0) return;
      waiting = [];
      emit();
    },

    state,
  };
};

export type JobQueue = ReturnType<typeof createJobQueue>;
